import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Button,
  Input,
  UncontrolledTooltip,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import {
  formatBytes,
  getFileExtension,
  getImageResolution,
  getFileAccessIcon,
  getFileAccessText,
} from '../../../helpers/Files';

import Avatar from '../../Avatar';
import DateTime from '../../DateTime';
import FileStatus from '../../File/Status';
import DuplicatePreviewItemCollections from './PreviewItemCollections';
import DuplicatePreviewItemCustomFields from './PreviewItemCustomFields';
import DuplicatePreviewItemTaxonomies from './PreviewItemTaxonomies';
import TrashRemainingDays from '../../TrashRemainingDays';

import ErrorBoundary from '../../ErrorBoundary';

const DuplicatePreviewItem = ({
  file,
  selected,
  handleSwitchSelected,
  handleArchiveFiles,
  handleDeleteFiles,
}) => {
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const extension = getFileExtension(file);
  const resolution = getImageResolution(file);
  const size = formatBytes(file.size);
  const fileTo = {
    pathname: `/files/${file.uuid}`,
    state: {
      modal: true,
      closeUrl: '/admin/duplicates',
    },
  };

  const intl = useIntl();

  const handleArchiveFile = () => {
    handleArchiveFiles([file.uuid]);
  };

  const handleDeleteFile = () => {
    handleDeleteFiles([file.uuid]);
  };

  return (
    <ErrorBoundary>
      <li id={`file-${file.uuid}`} className="duplicated-preview">
        <div className="duplicated-preview-wrapper">
          <div className="duplicated-preview-image-wrapper">
            <Input
              type="checkbox"
              className="input-check check-l"
              checked={selected}
              onChange={() => { handleSwitchSelected(file.uuid); }}
            />
            <Link to={fileTo}>
              <img className="duplicated-preview-img grid-item-img" src={file.urls.thumbnail} alt={file.display_name} />
            </Link>
          </div>
          <div className="duplicated-preview-body">
            <div className="duplicated-preview-header">
              <div className="duplicated-preview-header-content">
                <h5 className="duplicated-preview-title">
                  {file.display_name}
                  <FileStatus
                    file={file}
                  />
                </h5>
                <div className="duplicated-preview-file-details file-details">
                  <div>
                    <span className="file-access">
                      <i className={`fas ${getFileAccessIcon(file)}`} id={`MergeFileAccessTooltip-${file.uuid}`} />
                      <UncontrolledTooltip fade={false} placement="bottom" target={`MergeFileAccessTooltip-${file.uuid}`}>
                        {getFileAccessText(file, intl)}
                      </UncontrolledTooltip>
                    </span>
                    {resolution && (
                      <span className="file-resolution">
                        {resolution}
                      </span>
                    )}
                    {extension && (
                      <span className="file-extension">
                        {extension}
                      </span>
                    )}
                    {size && (
                      <span className="file-size">
                        {size}
                      </span>
                    )}
                    <TrashRemainingDays
                      file={file}
                    />
                  </div>
                </div>
                <div className="duplicated-preview-upload">
                  {intl.formatMessage({ defaultMessage: 'Uploaded' })}
                  {' '}
                  <DateTime
                    id={`file-date-${file.uuid}`}
                    className="collection-date"
                    dateTime={file.created_at}
                  />
                  {' '}
                  {file.user ? intl.formatMessage({ defaultMessage: 'by {name}' }, { name: file.user.name }) : intl.formatMessage({ defaultMessage: 'by [removed user]' })}
                  {(file.user && file.user.deactivated_at) && (
                    <>
                      {' '}
                      {intl.formatMessage({ defaultMessage: '(inactive)' })}
                    </>
                  )}
                </div>
                {file.archived && (
                  <div className="duplicated-preview-file-archived">
                    {intl.formatMessage({ defaultMessage: 'Archived file' })}
                  </div>
                )}
              </div>
              <div className="duplicated-preview-file-owner">
                <div className="user-avatar">
                  <Avatar user={file.user} expandable={true} id={file.uuid} />
                  <UncontrolledTooltip fade={false} placement="bottom" target={`avatar-${file.uuid}`}>
                    {file.user ? file.user.name : intl.formatMessage({ defaultMessage: '[removed user]' })}
                    {(file.user && file.user.deactivated_at) && (
                      <>
                        {' '}
                        {intl.formatMessage({ defaultMessage: '(inactive)' })}
                      </>
                    )}
                  </UncontrolledTooltip>
                </div>
              </div>
            </div>
            <div className="duplicated-preview-tools">
              <Button
                className="duplicated-preview-details-btn"
                onClick={() => { setDetailsExpanded(!detailsExpanded); }}
              >
                {intl.formatMessage({ defaultMessage: 'Details' })}
                {detailsExpanded ? (
                  <i className="fas fa-angle-up" />
                ) : (
                  <i className="fas fa-angle-down" />
                )}
              </Button>
              <ul className="duplicated-preview-tools-list">
                <li className="duplicated-preview-tool">
                  <Button
                    id={`delete-file-${file.uuid}`}
                    className="duplicated-preview-delete-btn"
                    onClick={handleDeleteFile}
                  >
                    <i className="fas fa-trash" />
                  </Button>
                  <UncontrolledTooltip placement="bottom" target={`delete-file-${file.uuid}`}>
                    {intl.formatMessage({ defaultMessage: 'Delete file' })}
                  </UncontrolledTooltip>
                </li>
                {!file.archived && (
                  <li className="duplicated-preview-tool">
                    <Button
                      id={`archive-file-${file.uuid}`}
                      className="duplicated-preview-archive-btn"
                      onClick={handleArchiveFile}
                    >
                      <i className="fas fa-archive" />
                    </Button>
                    <UncontrolledTooltip placement="bottom" target={`archive-file-${file.uuid}`}>
                      {intl.formatMessage({ defaultMessage: 'Archive file' })}
                    </UncontrolledTooltip>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
        {detailsExpanded && (
          <div className="duplicated-preview-details">
            <ul className="duplicated-preview-details-list">
              {file.description && (
                <li className="duplicated-preview-detail">
                  <h5>{intl.formatMessage({ defaultMessage: 'Description:' })}</h5>
                  <div className="duplicated-preview-detail-content">
                    <p>{file.description}</p>
                  </div>
                </li>
              )}

              <DuplicatePreviewItemCustomFields file={file} />

              <DuplicatePreviewItemCollections file={file} />

              <DuplicatePreviewItemTaxonomies file={file} />
            </ul>
          </div>
        )}
      </li>
    </ErrorBoundary>
  );
};

DuplicatePreviewItem.propTypes = {
  file: PropTypes.object.isRequired,
  selected: PropTypes.bool.isRequired,
  handleSwitchSelected: PropTypes.func.isRequired,
  handleArchiveFiles: PropTypes.func.isRequired,
  handleDeleteFiles: PropTypes.func.isRequired,
};

export default connect()(DuplicatePreviewItem);
